import React, { useState, useMemo } from 'react';
import { getImageUrl, getImageFallbackUrls, PLACEHOLDER_IMAGE } from '../utils/imageHelper';
import { cn } from '../lib/utils';
import { ImageOff } from 'lucide-react';

/**
 * ListingImage - SOW Compliant Image Component
 * 
 * Handles the dual storage strategy:
 * - Synced assets (product_image, variant_swatch, ad_creative, review_widget):
 *   Use Shopify CDN first, fallback to DO Spaces
 * - Internal assets (icons, dashboard, ui):
 *   Use DO Spaces directly
 * - Pre-publication assets:
 *   Use DO Spaces until Shopify CDN is available
 * 
 * Features:
 * - Automatic URL resolution based on asset type
 * - Multiple fallback attempts
 * - Graceful error handling with placeholder
 * - Lazy loading for performance
 */
const ListingImage = ({
  src,
  alt = 'Image',
  assetType = 'product_image',  // product_image, variant_swatch, ad_creative, review_widget, internal
  className = '',
  containerClassName = '',
  fallbackUrls = [],
  showPlaceholder = true,
  placeholderContent,
  onLoad,
  onError: externalOnError,
  ...props
}) => {
  const [currentSrc, setCurrentSrc] = useState(() => getImageUrl(src, assetType));
  const [failedUrls, setFailedUrls] = useState([]);
  const [hasError, setHasError] = useState(false);

  // Get all possible fallback URLs based on asset type
  const allFallbackUrls = useMemo(() => {
    const urls = [];
    
    // Primary URL from source
    const primary = getImageUrl(src, assetType);
    if (primary) urls.push(primary);
    
    // Auto-generated fallbacks based on asset type
    const autoFallbacks = getImageFallbackUrls(src, assetType);
    autoFallbacks.forEach(url => {
      if (!urls.includes(url)) urls.push(url);
    });
    
    // Additional provided fallbacks
    fallbackUrls.forEach(url => {
      const normalized = getImageUrl(url, assetType);
      if (normalized && !urls.includes(normalized)) urls.push(normalized);
    });
    
    return urls;
  }, [src, assetType, fallbackUrls]);

  const handleError = (e) => {
    const failedUrl = e.target.src;
    
    // Mark current URL as failed
    setFailedUrls(prev => [...prev, failedUrl]);
    
    // Find next URL to try
    const currentIndex = allFallbackUrls.indexOf(failedUrl);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < allFallbackUrls.length) {
      // Try next fallback URL
      const nextUrl = allFallbackUrls[nextIndex];
      setCurrentSrc(nextUrl);
    } else {
      // All URLs failed
      console.warn(`[ListingImage] All URLs failed for ${assetType}:`, allFallbackUrls);
      setHasError(true);
      if (externalOnError) externalOnError(e);
    }
  };

  const handleLoad = (e) => {
    setHasError(false);
    if (onLoad) onLoad(e);
  };

  // If no source or all URLs failed, show placeholder
  if (!currentSrc || hasError) {
    if (!showPlaceholder) return null;
    
    return (
      <div 
        className={cn(
          "flex items-center justify-center bg-gray-100 text-gray-400",
          containerClassName || className
        )}
        {...props}
      >
        {placeholderContent || (
          <div className="flex flex-col items-center gap-2">
            <ImageOff className="h-8 w-8" />
            <span className="text-xs">No image</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      loading="lazy"
      {...props}
    />
  );
};

/**
 * ProductImage - Specifically for product images (synced to Shopify CDN)
 */
export const ProductImage = (props) => (
  <ListingImage {...props} assetType="product_image" />
);

/**
 * VariantSwatchImage - For variant option images (synced to Shopify CDN)
 */
export const VariantSwatchImage = (props) => (
  <ListingImage {...props} assetType="variant_swatch" />
);

/**
 * InternalImage - For internal MCS assets (DO Spaces only, no Shopify sync)
 */
export const InternalImage = (props) => (
  <ListingImage {...props} assetType="internal" />
);

/**
 * ReviewImage - For review/rating images (synced to Shopify CDN)
 */
export const ReviewImage = (props) => (
  <ListingImage {...props} assetType="review_widget" />
);

/**
 * ListingImageCard - Image with card-style container
 */
export const ListingImageCard = ({
  src,
  alt,
  assetType = 'product_image',
  aspectRatio = 'square', // 'square', 'video', 'portrait'
  className = '',
  ...props
}) => {
  const aspectClasses = {
    square: 'aspect-square',
    video: 'aspect-video',
    portrait: 'aspect-[3/4]',
  };

  return (
    <div className={cn("relative overflow-hidden bg-gray-100 rounded-lg", aspectClasses[aspectRatio], className)}>
      <ListingImage
        src={src}
        alt={alt}
        assetType={assetType}
        className="absolute inset-0 w-full h-full object-cover"
        {...props}
      />
    </div>
  );
};

export default ListingImage;
